// memberDoubleClick — Phase 6.7 サブ task 後追い (consult 2026-05-07).
//
// 旧版動作: 参加者リストの名前をダブルクリックすると chat 入力欄に名前が
// 挿入される (mention っぽい使い方).
//
// 新版仕様 (consult 2026-05-07):
// - クリックされたユーザの **`displayName` を chat input の現在カーソル位置**
//   (caret) に挿入する. 旧版踏襲で **「@名前」ではなく素の名前のみ**.
// - caret 情報が取れない / undefined のときは **末尾に追記** で fallback.
// - 連続ダブルクリックで重複貼り付けされても問題ない (= 旧版踏襲、ユーザが
//   消せばよい).
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM** なので、`<input>` の
// selectionStart/selectionEnd を伴う React 操作は単体テストしにくい.
// 文字列計算だけを純粋関数 `insertNameIntoDraft` に切り出し、ChatPanel /
// MemberPanel 側の wiring は手動確認に倒す.
//
// (architecture.md §16, classic-ui-restore consult 2026-05-07)

/** 入力欄の caret 範囲. `null` のとき = caret 情報が取得できない (= 末尾追記). */
export interface CaretRange {
  readonly start: number;
  readonly end: number;
}

/**
 * draft 文字列の指定 caret 範囲に `displayName` を挿入した結果を返す.
 *
 * - `caret` が `null` のとき: draft 末尾に追記する (= 旧版互換、selection
 *   情報が取れない fallback 経路).
 * - `caret` が選択範囲のとき: 範囲内のテキストを displayName で置き換える.
 *   (= 旧版は単一 caret 想定だが、選択していたら置換する方が直感的).
 * - `caret.start > caret.end` の異常系は順序を入れ替えて扱う (defensive).
 *
 * 戻り値:
 * - `nextDraft`: 挿入後の draft 文字列.
 * - `nextCaret`: 挿入後にあるべき caret 位置 (= 挿入文字列の直後). caller は
 *   これを `<input>.setSelectionRange` で復元する.
 */
export const insertNameIntoDraft = (
  draft: string,
  displayName: string,
  caret: CaretRange | null,
): { readonly nextDraft: string; readonly nextCaret: number } => {
  if (caret === null) {
    // Fallback: 末尾追記.
    return {
      nextDraft: draft + displayName,
      nextCaret: draft.length + displayName.length,
    };
  }
  // Defensive: range の順序を normalize.
  const start = Math.max(0, Math.min(caret.start, draft.length));
  const end = Math.max(0, Math.min(caret.end, draft.length));
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const before = draft.slice(0, lo);
  const after = draft.slice(hi);
  const nextDraft = before + displayName + after;
  return {
    nextDraft,
    nextCaret: lo + displayName.length,
  };
};
